/**
 * @file src/helpers/ory/utils/getEmailSuggestion.ts
 * @exports getEmailSuggestion
 * @summary Analyzes an email string and returns a specific, actionable suggestion for common formatting errors. Returns null if the email has no detectable structural issues. Improves WCAG 2.1 AA SC 3.3.3 compliance by offering error suggestions rather than generic "Invalid email" messages.
 */

const SPECIAL_CHAR_REGEX = /[ '"\(\),;:<>\[\]\\|?=&#!/]/;

export const EMAIL_ERROR_CONSECUTIVE_PERIODS = "Email should not contain consecutive periods.";
export const EMAIL_ERROR_DOMAIN_DOT = "Email domain should not start or end with a period.";
export const EMAIL_ERROR_EMPTY_DOMAIN = 'Email must have a domain after the "@" symbol';
export const EMAIL_ERROR_EMPTY_LOCAL = 'Email must have a username before the "@" symbol.';
export const EMAIL_ERROR_INCOMPLETE_TLD = "Email domain extension appears incomplete (e.g., .com, .org).";
export const EMAIL_ERROR_LOCAL_DOT = "Email username should not start or end with a period.";
export const EMAIL_ERROR_MISSING_AT = 'Email must contain an "@" symbol';
export const EMAIL_ERROR_MULTIPLE_AT = 'Email must contain only one "@" symbol.';

/**
 * Analyzes an email string and returns a specific suggestion for common
 * formatting errors. Returns null if no structural issue is detected.
 *
 * Important: this function is intentionally conservative — it only flags
 * patterns that are definitively invalid. Theoretically-valid-but-unusual
 * addresses do NOT trigger a suggestion.
 */
export function getEmailSuggestion(email: string): string | null {
	// 1. Special characters
	const specialCharMatch = email.match(SPECIAL_CHAR_REGEX);
	if (specialCharMatch) {
		const char = specialCharMatch[0];
		const charLabel = char === " " ? "space" : char;
		return `Email should not contain special character: ${charLabel}`;
	}

	// 2. Missing @
	if (!email.includes("@")) {
		return EMAIL_ERROR_MISSING_AT;
	}

	// 3. Multiple @
	const atCount = (email.match(/@/g) || []).length;
	if (atCount > 1) {
		return EMAIL_ERROR_MULTIPLE_AT;
	}

	const atIndex = email.indexOf("@");
	const localPart = email.slice(0, atIndex);
	const domain = email.slice(atIndex + 1);

	// 4. Empty local part
	if (!localPart) {
		return EMAIL_ERROR_EMPTY_LOCAL;
	}

	// 5. Empty domain
	if (!domain) {
		return EMAIL_ERROR_EMPTY_DOMAIN;
	}

	// 6. Missing TLD / extension (domain has no dot)
	if (!domain.includes(".")) {
		return `Did you mean ${email}.com?`;
	}

	// 7. Leading or trailing dots in domain
	if (domain.startsWith(".") || domain.endsWith(".")) {
		return EMAIL_ERROR_DOMAIN_DOT;
	}

	// 8. Incomplete TLD (single character after last dot)
	const lastDotIndex = domain.lastIndexOf(".");
	const tld = domain.slice(lastDotIndex + 1);
	if (tld.length < 2) {
		return EMAIL_ERROR_INCOMPLETE_TLD;
	}

	// 9. Consecutive dots anywhere
	if (email.includes("..")) {
		return EMAIL_ERROR_CONSECUTIVE_PERIODS;
	}

	// 10. Leading or trailing dots in local part
	if (localPart.startsWith(".") || localPart.endsWith(".")) {
		return EMAIL_ERROR_LOCAL_DOT;
	}

	return null;
}
